import React, { useState } from 'react';
import { Button, Modal, Spinner, Select, TextInput } from 'flowbite-react';
import { RxCross2 } from 'react-icons/rx';
import { UseMutationResult } from 'react-query';
import { BaseAPIResponse, CreateApplicationInput, UIExtendedApplication, OptOutReason } from 'types';

interface Props {
    application?: UIExtendedApplication;
    isOpen: boolean;
    onClose: () => void;
    optOutOfApplicationProcessMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
}

// Helper function to format enum values for display
const formatOptOutReason = (reason: OptOutReason): string => {
    return reason
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Get all enum values except 'OTHER' for the dropdown
const getDropdownOptions = (): { value: OptOutReason; label: string }[] => {
    return Object.values(OptOutReason)
        .filter(reason => reason !== OptOutReason.OTHER)
        .map(reason => ({
            value: reason,
            label: formatOptOutReason(reason)
        }));
};

const NotInterestedModal = ({ isOpen, onClose, optOutOfApplicationProcessMutation, application }: Props) => {
    const [selectedReason, setSelectedReason] = useState<OptOutReason | ''>('');
    const [otherRemarks, setOtherRemarks] = useState<string>('');
    const [errors, setErrors] = useState<{ reason?: string; remarks?: string }>({});

    const dropdownOptions = getDropdownOptions();

    const handleSubmit = () => {
        // Reset errors
        setErrors({});

        // Validate reason is selected
        if (!selectedReason) {
            setErrors({ reason: 'Please select a reason' });
            return;
        }

        // Validate remarks if "other" is selected
        if (selectedReason === OptOutReason.OTHER && !otherRemarks.trim()) {
            setErrors({ remarks: 'Please specify the reason' });
            return;
        }

        // Submit the form
        optOutOfApplicationProcessMutation.mutate({
            createInfo: {
                position: application?.position?._id.toString() || '',
                user: application?.user?._id || '',
                status: application?.status,
                optOutReason: selectedReason,
                optOutRemarks: selectedReason === OptOutReason.OTHER ? otherRemarks.trim() : undefined,
            },
        });
    };

    const handleClose = () => {
        setSelectedReason('');
        setOtherRemarks('');
        setErrors({});
        onClose();
    };

    return (
        <div>
            <Modal position={'center'} show={isOpen} dismissible={true} onClose={handleClose} size={'md'}>
                <Modal.Body className="relative flex flex-col gap-y-8 justify-center rounded-[8px] border border-solid dark:border-[#555770] dark:bg-[#1F2A37] p-8">
                    <div>
                        <p className="text-center dark:text-[#F2F2F5] font-inter text-[16px] font-[500] leading-[24px] mb-6">
                            Please let us know why you're not interested in this position
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Reason for not being interested *
                            </label>
                            <Select
                                value={selectedReason}
                                onChange={(e) => setSelectedReason(e.target.value as OptOutReason)}
                                className={errors.reason ? 'border-red-500' : ''}
                            >
                                <option value="">Select a reason</option>
                                {/* Dynamically generate options from enum */}
                                {dropdownOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                                {/* Add "Other" option at the end */}
                                <option value={OptOutReason.OTHER}>Other</option>
                            </Select>
                            {errors.reason && (
                                <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
                            )}
                        </div>

                        {selectedReason === OptOutReason.OTHER && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Please specify the reason *
                                </label>
                                <TextInput
                                    value={otherRemarks}
                                    onChange={(e) => setOtherRemarks(e.target.value)}
                                    placeholder="Please specify your reason..."
                                    className={errors.remarks ? 'border-red-500' : ''}
                                />
                                {errors.remarks && (
                                    <p className="text-red-500 text-sm mt-1">{errors.remarks}</p>
                                )}
                            </div>
                        )}

                        <div className="mt-[20px] flex items-center gap-[16px] justify-center">
                            <Button
                                color="purplePlaced"
                                onClick={handleSubmit}
                                disabled={optOutOfApplicationProcessMutation.isLoading}
                            >
                                {optOutOfApplicationProcessMutation.isLoading ? <Spinner /> : 'Submit'}
                            </Button>
                            <Button
                                onClick={handleClose}
                                className="dark:!bg-[#4B5563] bg-white dark:hover:!bg-[#566171] hover:!bg-blue-100 !border dark:!border-[#6B7280] border-blue-500 text-blue-600 dark:text-white"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                    <RxCross2
                        onClick={handleClose}
                        className="absolute right-[10px] top-[10px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]"
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default NotInterestedModal;
